import { drugAction } from '@api'
import { dosageFormOptions, drugRouteOptions } from '@data'
import { DropDownMenu } from '@doctor/Encounter/Manage Orders/Modals/AddMedication'
import { useDrugs, useMedication, useStaff } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { frequencySchema, medicationSchema } from '@schema/pDirectory.schema'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { hideEditMedicationFrequency, showEditMedicationFrequency } from '@store/slicers/ModalSlicer'
import { palette } from '@themes/palette'
import { FrequencyData } from '@type/medication.type'
import {
  DateInputField,
  FormButton,
  FormInputField,
  Input,
  Loader,
  Modal,
  ModalHeader,
  RadioBtn,
  SearchInput,
  SelectInputField,
  TextAreaField,
  TimeInputField,
  ToggleBtn
} from '@ui'
import { Select } from '@ui/SearchInput/Select'
import {
  capitalizeDrugName,
  concatenateStrengthsConcise,
  getError,
  GetFrequencyName,
  normalizeHourlyFrequency,
  removeEmptyValues,
  showToast
} from '@util'
import { useCustomOptionStore } from '@zustore/store'
import { Box, CheckBox, Form, Layer, Text } from 'grommet'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FaPlus } from 'react-icons/fa'
import { useParams } from 'react-router-dom'
import { InferType } from 'yup'

type Props = {
  onClose: () => void
  defaultData?: any
  id?: string
}

const renderFrequencyOptions = (frequencyData: FrequencyData): string => {
  if (!frequencyData) {
    return ''
  }

  const { interval = '', periods = [], type = '' } = frequencyData

  if (type === 'hourly') {
    return interval
  } else if (type === 'daily') {
    if (periods && periods?.length > 0) {
      const formattedPeriods =
        periods?.length > 2
          ? periods.slice(0, -1).join(', ') + ', and ' + periods[periods?.length - 1]
          : periods.join(' and ')
      return `Every ${formattedPeriods}`
    } else {
      return 'Daily'
    }
  }
  return ''
}

export const EditNurseMed: React.FC<Props> = ({ onClose: handleClose, defaultData, id }) => {
  const { encounterId, patientId } = useParams<string>()
  const { user } = useAppSelector(state => state.user)
  const { dosage_unit } = useAppSelector(state => state.config)
  const { singleStaff } = useStaff({ id: user?.authable_id })
  const { updateMedication, medFrequencies, medication, includedMed, singleMedIsLoading } = useMedication({ id })
  const { edit } = useAppSelector(state => state.modal.medication.custom_frequency)

  const includedDatas = useMemo(() => {
    if (medication && includedMed) {
      const allergy = includedMed.find(
        ({ type, id }) => type === 'allergy' && id === medication.relationships?.allergy?.data?.id
      )
      const icd_codes = includedMed.filter(
        ({ id, type }) => type === 'icd_code' && allergy?.relationships?.icd_codes?.data?.some(icd => icd?.id === id)
      )
      return { allergy, icd_codes }
    }
    return { allergy: undefined, icd_codes: [] }
  }, [medication, includedMed])

  const methods = useForm<InferType<typeof medicationSchema>>({
    resolver: yupResolver(medicationSchema),
    values: {
      ...medication?.attributes,
      allergy_end_date: includedDatas?.allergy?.attributes?.end_date,
      allergy_start_date: includedDatas?.allergy?.attributes.start_date,
      comment: medication?.attributes?.comment ?? '',
      dose_quantity: medication?.attributes?.dose_quantity || '',
      dose_unit: medication?.attributes?.dose_unit || '',
      strength: medication?.attributes?.strength || concatenateStrengthsConcise(medication?.attributes?.api_doses),
      end_at: medication?.attributes?.end_at || '',
      form: medication?.attributes?.form || '',
      icd_codings_attributes: includedDatas?.icd_codes?.map(({ id }) => ({
        icd_code_id: id
      })),
      medication_type: medication?.attributes?.medication_type?.toUpperCase() === 'STAT' ? 'STAT' : 'Routine',
      nf_drug_name: medication?.attributes?.nf_drug_name || '',
      once: medication?.attributes?.once || false,
      reaction: includedDatas?.allergy?.attributes.reaction,
      route: medication?.attributes?.route || '',
      start_at: medication?.attributes?.start_at || '',
      title: includedDatas?.allergy?.attributes.title
    }
  })

  const { watch } = methods
  const formValue = watch('form')

  const freq_methods = useForm({
    defaultValues: {
      ...defaultData?.attributes.custom_frequency
    },
    resolver: yupResolver(frequencySchema)
  })
  const watchData = freq_methods.watch(['type', 'interval', 'periods', 'specific_times'])

  const dispatch = useAppDispatch()
  const [freq_data, setFreqData] = useState<FrequencyData>({
    interval: watchData[1] || '',
    periods: (watchData[2] || []).filter((period: any): period is string => period !== undefined),
    specific_times: (watchData[3] || []).filter((time: any): time is string => time !== undefined),
    type: watchData[0] === 'hourly' || watchData[0] === 'daily' ? watchData[0] : 'hourly'
  })
  const [, setIsPeriodOfDay] = useState(false)
  const [isHourly, setIsHourly] = useState(true)
  const [, setSetIsEnabled] = useState(true)
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    GetFrequencyName(defaultData?.relationships?.medication_frequency?.data?.id) || ''
  )
  const { isCustomFrequencySelected, setIsCustomFrequencySelected } = useCustomOptionStore()
  const [customOptions, setCustomOptions] = useState<any[]>([])

  const [doseUnit, setDoseUnit] = useState(medication?.attributes?.dose_unit || 'mg')

  const { drugSearch, createDrug } = useDrugs({ query: watch('nf_drug_name'), form: watch('form') })

  const availableStrengthOptions = useMemo(() => {
    const baseOptions =
      drugSearch?.flatMap(
        drug =>
          drug.available_strengths?.map(combination => {
            // 1. Create the user-friendly label by joining the strengths
            const labels = combination?.map(s => s.strength)
            const combinedLabel = labels.join('/')

            // 2. The value will be the original combination array of objects
            return {
              label: combinedLabel,
              value: combinedLabel
            }
          }) ?? []
      ) ?? []

    return [...baseOptions, ...customOptions]
  }, [drugSearch, customOptions])

  const strengthValue = watch('strength')

  const strengthVal = useMemo(() => {
    if (strengthValue) {
      return { label: strengthValue, value: strengthValue }
    } else {
      return { label: '', value: 'strengthValue' }
    }
  }, [strengthValue])

  const {
    formState: { isSubmitting }
  } = methods

  const [toggleStates, setToggleStates] = useState({
    Period: false,
    Specific_time: false,
    once: false
  })

  const DrugValue = watch('nf_drug_name')
  const value = useMemo(() => {
    if (DrugValue) {
      return { label: capitalizeDrugName(DrugValue), value: DrugValue }
    } else {
      return { label: '', value: 'DrugValue' }
    }
  }, [DrugValue])

  const handleToggle = (id: string, state: boolean) => {
    setToggleStates(prevState => {
      const newToggles = { ...prevState }
      if (id === 'Period') {
        newToggles.Period = state
        if (state) {
          newToggles.Specific_time = false
        }
      } else if (id === 'Specific_time') {
        newToggles.Specific_time = state
        if (state) {
          newToggles.Period = false
        }
      }
      return newToggles
    })
  }

  const handleHourlyCheck = () => {
    setToggleStates({
      Period: false,
      Specific_time: false,
      once: toggleStates.once
    })
    setIsHourly(true)
    setIsPeriodOfDay(false)

    freq_methods.setValue('periods', [])
    freq_methods.setValue('specific_times', [])
  }

  // *** MODIFIED FUNCTION ***
  const handleDailyCheck = () => {
    // 1. Update UI state
    setToggleStates({
      Period: true,
      Specific_time: false,
      once: toggleStates.once
    })
    setIsHourly(false)
    setIsPeriodOfDay(true)

    // 2. CRITICAL: Clear the irrelevant 'interval' field from the form state
    freq_methods.setValue('interval', '')
  }

  const set = useMemo(() => {
    const [type, interval, specific_times, periods] = watchData
    if (type === 'hourly') {
      return !interval
    } else if (type === 'daily') {
      return (!specific_times || specific_times?.length === 0) && (!periods || periods?.length === 0)
    }
    return true
  }, [watchData])

  useEffect(() => {
    setSetIsEnabled(!set)
  }, [set])

  const onFrequencyModalClose = () => {
    dispatch(hideEditMedicationFrequency())
  }

  const transformFormData = (data: any): FrequencyData => {
    return {
      ...data,
      periods: data.periods?.filter((period: string | undefined) => period !== undefined) || [],
      specific_times: data.specific_times?.filter((time: string | undefined) => time !== undefined) || []
    }
  }

  const onFreqSubmit = (data: any) => {
    const transformedData = transformFormData(data)
    // Normalize the interval if it's hourly frequency
    if (transformedData.type === 'hourly' && transformedData.interval) {
      const normalized = normalizeHourlyFrequency(transformedData.interval)
      if (normalized) {
        transformedData.interval = normalized
      }
    }
    setFreqData(transformedData)
    setSelectedFrequency(renderFrequencyOptions(transformedData))
    setIsCustomFrequencySelected(true)
    onFrequencyModalClose()
  }

  const [timeFields, setTimeFields] = useState<string[]>([''])

  const addTimeField = () => {
    setTimeFields([...timeFields, `specific_times_${timeFields?.length}`])
  }

  const onSubmit = async (data: any) => {
    const {
      dose_quantity,
      dosage,
      icd_codings_attributes,
      allergy_end_date,
      allergy_start_date,
      reaction,
      title,
      status,
      ...attributes
    } = data
    delete (attributes as any).api_doses

    const customFrequency = isCustomFrequencySelected ? removeEmptyValues(freq_data!) : {}
    const frequencyId = isCustomFrequencySelected
      ? null
      : medFrequencies!.find(freq => freq.attributes.name === selectedFrequency)?.id
    const payload = {
      data: {
        attributes: {
          ...attributes,
          custom_frequency: customFrequency,
          dosage_unit: doseUnit,
          dose_quantity,
          ordered: true,
          prescribed: false,
          ...(icd_codings_attributes && icd_codings_attributes?.length > 0
            ? {
                allergy_attributes: {
                  end_date: allergy_end_date,
                  icd_codings_attributes,
                  id: defaultData?.relationships?.allergy?.data?.id,
                  patient_id: patientId,
                  reaction,
                  start_date: allergy_start_date,
                  status,
                  title
                }
              }
            : {
                allergy_attributes: { _destroy: !(icd_codings_attributes?.length > 0) }
              })
        },
        relationships: {
          encounter: {
            data: {
              id: encounterId,
              type: 'encounter'
            }
          },
          staff: {
            data: {
              id: `${singleStaff?.id}`,
              type: 'staff'
            }
          },
          // drug: {
          //   data: {
          //     id: drug_id,
          //     type: 'drug'
          //   }
          // },
          ...(frequencyId
            ? {
                medication_frequency: {
                  data: {
                    id: frequencyId,
                    type: 'medication_frequency'
                  }
                }
              }
            : '')
        },
        type: 'medication'
      }
    }

    try {
      await updateMedication({ formVal: payload, id: id || '' })
      showToast('Medication updated successfully')
      handleClose()
    } catch (error) {
      getError(error)
    }
  }
  const handleCustomSelect = () => {
    dispatch(
      showEditMedicationFrequency({
        defaultData: defaultData!.attributes.custom_frequency,
        id: defaultData?.id
      })
    )
  }

  const handleOptionSelect = (value: string) => {
    setSelectedFrequency(value)
    setFreqData({ interval: value, periods: [], specific_times: [], type: 'hourly' })
  }

  return (
    <>
      <FormProvider {...methods}>
        <Modal onClose={handleClose} title="Edit Medication">
          <Form
            onSubmit={methods.handleSubmit(onSubmit)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '550px',
              overflow: 'auto',
              position: 'relative'
            }}
          >
            {singleMedIsLoading && (
              <div className="absolute flex justify-center items-center inset-0 z-10">
                <Loader />
              </div>
            )}
            <Box overflow={'auto'} pad={'24px'}>
              <Box flex={false} gap={'12px'}>
                <Box>
                  {/* <SelectInputField
                      name="nf_drug_name"
                      options={drugOptions || []}
                      label="Drug"
                      onSearch={drugSearch}
                      labelKey="name"
                      valueKey={{ key: 'id', reduce: true }}
                    /> */}
                  <Controller
                    control={methods.control}
                    name="nf_drug_name"
                    render={({ field: { onChange } }) => (
                      <Select
                        borderRadius={'25px'}
                        defaultOptions
                        getOptionValue={({ value }) => value}
                        isAsync
                        isCreatable
                        label="Drug name"
                        loadOptions={drugAction}
                        onChange={(val: any) => onChange(val?.value)}
                        value={value}
                      />
                    )}
                  />
                </Box>
                {/* <FormInputField counter name="quantity" placeholder="" label="Quantity" type="number" /> */}
                <Box className="">
                  <SelectInputField
                    className="capitalize"
                    label="Form"
                    name="form"
                    options={dosageFormOptions || []}
                    placeholder="Choose dosage form"
                  />
                </Box>
                <Controller
                  control={methods.control}
                  name="strength"
                  render={({ field: { onChange, value }, fieldState: { error } }) => {
                    const selectedOption = value
                      ? availableStrengthOptions.find(option => option?.value === value) || { label: value, value }
                      : null

                    const handleCreateDrug = async (value: string) => {
                      if (!DrugValue || !formValue) {
                        showToast('Please select drug name and drug form before creating a custom strength')
                        return
                      }

                      // Regex to validate format like "100mg", "10.5ml", or "100mg + 200mg"
                      const strengthRegex =
                        /^\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+(?:\s*\+\s*\d+(?:\.\d+)?\s*[a-zA-Z0-9%/]+)*$/
                      if (!strengthRegex.test(value)) {
                        showToast('Invalid format. Use "100mg" or "100mg + 200mg"')
                        return
                      }

                      const payload = {
                        data: {
                          type: 'drug',
                          attributes: {
                            name: DrugValue,
                            form: formValue,
                            strength: value
                          }
                        }
                      }
                      try {
                        await createDrug(payload)
                        setCustomOptions(prev => [...prev, { label: value, value }])
                        onChange(value)
                      } catch (error) {
                        getError(error)
                      }
                    }

                    const handleChange = (newValue: any) => {
                      if (!newValue) {
                        onChange(null) // Handle clearing the select
                        return
                      }

                      if (typeof newValue === 'string') {
                        onChange(newValue)
                      } else if (newValue && newValue?.value) {
                        onChange(newValue?.value)
                      }
                    }

                    return (
                      <Select
                        borderRadius={'25px'}
                        createOptionPosition="first"
                        defaultOptions
                        error={error?.message}
                        getOptionValue={(option: any) => option.value}
                        isCreatable
                        label="Available Strength(s) / Calculated Dosage"
                        onChange={handleChange} // <-- USE OUR CUSTOM HANDLER
                        onCreateOption={handleCreateDrug}
                        options={availableStrengthOptions}
                        placeholder="eg 100mg + 200mg or 500mg"
                        value={selectedOption}
                      />
                    )
                  }}
                />
                <Box className="!min-h-[auto]">
                  <Text margin={{ bottom: '2px' }} style={{ fontSize: '14px', fontWeight: 500 }}>
                    Dosage (Form Based)
                  </Text>

                  <Box
                    align="center"
                    border={{ color: '#999999', side: 'all' }}
                    direction="row"
                    round={'100px'}
                    width={'100%'}
                  >
                    <Controller
                      control={methods.control}
                      name="dose_quantity"
                      render={({ field: { value, onChange, onBlur, name, ref } }) => (
                        <Input
                          name={name}
                          onBlur={onBlur}
                          onChange={e => onChange(e.target.value)}
                          placeholder="Enter dosage"
                          ref={ref}
                          style={{ border: '0', height: '100%', padding: '', width: '342px' }}
                          type="number"
                          value={value == null ? '' : String(value)}
                        />
                      )}
                    />

                    <Box className="borderless !items-center !border-none" direction="row">
                      <div className="borderless h-8"></div>
                      <SearchInput
                        dropProps={{
                          className: '!left-[407.667px] !w-[420.1px] '
                        }}
                        onChange={val => setDoseUnit(val?.value)}
                        options={dosage_unit.filter(unit => unit !== 'ml') || []}
                        placeholder="unit"
                        setValue={setDoseUnit}
                        style={{ border: 'none', outline: 0, padding: '', textTransform: 'lowercase' }}
                        value={doseUnit}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box className="">
                  <SelectInputField
                    className="capitalize"
                    label="Route"
                    name="route"
                    options={drugRouteOptions || []}
                    placeholder="Choose route"
                  />
                </Box>{' '}
                <Box className="grid gap-y-2">
                  <Box className="">
                    <DropDownMenu
                      label="Frequency"
                      onCustomSelect={handleCustomSelect}
                      onOptionSelect={handleOptionSelect}
                      selectedValue={renderFrequencyOptions(freq_data!) || selectedFrequency}
                    />
                  </Box>
                  <Controller
                    control={methods.control}
                    defaultValue={false}
                    name="once"
                    render={({ field }) => (
                      <Box align="center" direction="row" gap="small">
                        <CheckBox
                          checked={field?.value}
                          label="Once"
                          onChange={e => {
                            field.onChange(e.target.checked)
                          }}
                        />
                      </Box>
                    )}
                  />
                </Box>
                <Box className="">
                  <SelectInputField
                    label="Urgency"
                    name="medication_type"
                    options={['STAT', 'Routine']}
                    placeholder="Select preferred regimen"
                  />
                </Box>
                <div>
                  <DateInputField
                    className=""
                    dateFormat="MMMM d, yyyy HH:mm"
                    label="Start Date and Time"
                    name="start_at"
                    showTimeSelect
                  />
                </div>
                <div>
                  <DateInputField
                    dateFormat="MMMM d, yyyy HH:mm"
                    label="End Date and Time"
                    name="end_at"
                    showTimeSelect
                  />
                </div>
                <Box className="">
                  <TextAreaField label="Note (optional)" name="comment" placeholder="Enter note" />
                </Box>
              </Box>
            </Box>
            <FormButton isLoading={isSubmitting} onClose={handleClose} />
          </Form>
        </Modal>
      </FormProvider>
      {edit.show && (
        <FormProvider {...freq_methods}>
          <Layer
            className=""
            modal
            onClickOutside={onFrequencyModalClose}
            onEsc={onFrequencyModalClose}
            position="center"
            style={{ borderRadius: '15px' }}
          >
            <Form onSubmit={freq_methods.handleSubmit(onFreqSubmit)}>
              <Box style={{ maxHeight: '80vh', width: 'min(500px, calc(100vw - 1.5rem))' }}>
                <ModalHeader onClose={onFrequencyModalClose} title="Set Frequency" />
                <div className="grid gap-y-[1.7em] overflow-auto  p-6">
                  <Box className="">
                    <Controller
                      control={freq_methods.control}
                      defaultValue="hourly"
                      name="type"
                      render={({ field }) => (
                        <>
                          <Box margin={{ top: '12px' }}>
                            <Box align="center" direction="row" gap={'12px'} id={'hourly'} pad={{ bottom: '12px' }}>
                              <RadioBtn
                                {...field}
                                checked={'hourly' === field?.value}
                                onChange={() => {
                                  field.onChange('hourly')
                                  setIsHourly(true)
                                  setIsPeriodOfDay(false)
                                  handleHourlyCheck()
                                }}
                                value={'hourly'}
                              />
                              <Text
                                color={isHourly ? '#1A1A1A' : '#3D3D3D'}
                                size="small"
                                style={{ textTransform: 'capitalize' }}
                              >
                                {'Hourly'}
                              </Text>
                            </Box>
                          </Box>
                        </>
                      )}
                    />
                    <Box className="">
                      <FormInputField
                        disabled={!isHourly}
                        label="Intervals"
                        labelClassName={`${!isHourly ? '!text-[#1A1A1A] !opacity-50' : '!text-[#3D3D3D]'} `}
                        name="interval"
                        placeholder="E.g every 5 hours"
                        style={{ color: isHourly ? '#1A1A1A' : '#3D3D3D', opacity: isHourly ? 1 : 0.5 }}
                      />
                    </Box>
                  </Box>
                  <Box>
                    <Controller
                      control={freq_methods.control}
                      name="type"
                      render={({ field }) => (
                        <>
                          <Box className="!min-h-[auto]" margin={{ top: '12px' }}>
                            <Box align="center" direction="row" gap={'12px'} id={'daily'} pad={{ bottom: '12px' }}>
                              <RadioBtn
                                {...field}
                                checked={'daily' === field?.value}
                                onChange={() => {
                                  field.onChange('daily')
                                  setIsHourly(false)
                                  setIsPeriodOfDay(true)
                                  handleDailyCheck()
                                }}
                                value={'daily'}
                              />
                              <Text
                                color={!isHourly ? '#1A1A1A' : '#3D3D3D'}
                                size="small"
                                style={{ textTransform: 'capitalize' }}
                              >
                                {'Daily'}
                              </Text>
                            </Box>
                          </Box>
                        </>
                      )}
                    />
                    <Box
                      border={{ color: '#999999' }}
                      className="!min-h-[auto]"
                      gap={'.9em'}
                      height={'fit-content'}
                      pad={'.9em'}
                      round="8px"
                      style={{ opacity: !isHourly ? 1 : 0.5 }}
                    >
                      <div style={{ opacity: !toggleStates.Specific_time ? 1 : 0.5 }}>
                        <Box alignContent="center" className="justify-between" direction="row">
                          <Text color={!isHourly ? '#1A1A1A' : '#3D3D3D'} size="16px">
                            Period of the day
                          </Text>
                          <ToggleBtn
                            disabled={isHourly}
                            id="Period"
                            isToggledOn={toggleStates.Period}
                            onToggle={handleToggle}
                          />
                        </Box>
                        <Box>
                          <Controller
                            control={freq_methods.control}
                            name="periods"
                            render={({ field, fieldState: { error } }) => (
                              <>
                                <Box margin={{ top: '12px' }}>
                                  {['morning', 'afternoon', 'night']?.map(list => (
                                    <Box
                                      align="center"
                                      direction="row"
                                      gap={'12px'}
                                      id={list}
                                      key={list}
                                      pad={{ bottom: '15px' }}
                                    >
                                      <CheckBox
                                        {...field}
                                        checked={field?.value?.includes(list)}
                                        disabled={!toggleStates.Period || isHourly}
                                        onChange={e => {
                                          const newValue = e.target.checked
                                            ? [...(field?.value || []), list]
                                            : field?.value?.filter((val: string | undefined) => val !== list)
                                          field.onChange(newValue)
                                        }}
                                        value={list}
                                      />
                                      <Text
                                        color={!toggleStates.Period || isHourly ? '#3D3D3D' : '#1A1A1A'}
                                        size="small"
                                        style={{ textTransform: 'capitalize' }}
                                      >
                                        {list}
                                      </Text>
                                    </Box>
                                  ))}
                                </Box>
                                <Box pad={{ left: 'small' }}>
                                  <Text color="status-error" size="small">
                                    {error?.message}
                                  </Text>
                                </Box>
                              </>
                            )}
                          />
                        </Box>
                      </div>
                      <Box style={{ opacity: !toggleStates.Period ? 1 : 0.5 }}>
                        <Box alignContent="center" className="justify-between pb-4" direction="row">
                          <Text color={!toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D'} size="16px">
                            Specific time of the day
                          </Text>
                          <ToggleBtn
                            disabled={isHourly}
                            id="Specific_time"
                            isToggledOn={toggleStates.Specific_time}
                            onToggle={handleToggle}
                          />
                        </Box>
                        <div className="grid gap-y-3">
                          {timeFields?.map((field, index) => (
                            <div className="!h-fit" key={index}>
                              <TimeInputField
                                className="uppercase"
                                disabled={!toggleStates.Specific_time || isHourly}
                                label={``}
                                name={`specific_times.${index}`}
                                style={{
                                  color: toggleStates.Specific_time ? '#1A1A1A' : '#3D3D3D',
                                  opacity: toggleStates.Specific_time ? 1 : 0.5
                                }}
                              />
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-x-[5px] items-center mt-2" onClick={addTimeField}>
                          <FaPlus color={palette.brand} />
                          <Text color={palette.brand}>Add time</Text>
                        </div>
                      </Box>
                    </Box>
                  </Box>
                </div>
                <FormButton onClose={onFrequencyModalClose} submit="Set" />
              </Box>
            </Form>
          </Layer>
        </FormProvider>
      )}
    </>
  )
}
